import { NextPage } from 'next';
import { useTranslation, WithTranslation } from 'react-i18next';
import { Store } from 'redux';
import { LogicMiddleware } from 'redux-logic';
import Head from 'next/head';
import { useCallback, useState } from 'react';

import Layout from 'modules/layout';
import { loadPro, proSelector } from 'modules/users/duck';
import ProProfileContent from 'modules/users/modules/profiles/components/ProProfileContent';
import ProfileClaim from 'modules/users/modules/profiles/components/ProfileClaim';
import { getFAQStructuredData } from 'modules/marketing-pages/components/LandingPage/LandingPage/LandingPage';
import { RootState, wrapper } from 'store/index';
import { setCachedStates } from 'store/actions';
import { setupPageAndLoadTranslations } from 'utils/page';
import type { InternalBlogPostItem, SeoObject } from 'modules/marketing-pages/marketing-page.types';
import {
  JobProfile,
  QuestionAnswer,
  EnabledJobTypesSelectorProps,
  ReviewContent,
} from 'src/modules/users/modules/profiles/types';

import FeatureFlagsService from 'src/services/feature-flags';
import JobTypeAliasesService from 'src/services/job-type-alias';
import { getSSRCache, setSSRCache } from 'src/ssr-cache';
import { getInternalBlogPostsByJobTypeId } from 'modules/marketing-pages/service';
import { createCanonicalUrl, shouldAddNoindex } from 'utils/seo/crawler-control';

const featureFlagsService = FeatureFlagsService.getInstance();
const jobTypeAliasesService = JobTypeAliasesService.getInstance();

interface Props extends WithTranslation {
  featureFlags: { isReviewsQAEnabled: boolean };
  internalBlogPosts: Array<InternalBlogPostItem>;
  jobProfiles: Array<JobProfile>;
  jobTypeId?: string;
  jobTypeTitle?: string;
  questionAnswers?: Array<QuestionAnswer>;
  seo: SeoObject;
  slugFromQueryParam: string;
  title: string;
}

// title for service profile page
const getJobTypeTitle = (jobTypeId: string, jobProfiles: Array<JobProfile>) => {
  const jobType = jobProfiles.find((item) => item.jobTypeId === jobTypeId);
  return jobType?.label ?? '';
};

const ProProfilePage: NextPage<Props> = ({
  featureFlags,
  internalBlogPosts,
  jobProfiles,
  jobTypeId,
  jobTypeTitle,
  questionAnswers,
  seo,
  slugFromQueryParam,
  title,
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const [pageTitle, setPageTitle] = useState(jobTypeTitle ? `${title} | ${jobTypeTitle}` : title);

  const setJobTypeTitle = useCallback(
    (jobTypeIdParam?: string) => {
      if (jobTypeIdParam) {
        const jobTypeName = getJobTypeTitle(jobTypeIdParam, jobProfiles);
        setPageTitle(`${title}${jobTypeName ? ` | ${jobTypeName}` : ''}`);
      } else {
        setPageTitle(title);
      }
    },
    [jobProfiles, title]
  );

  return (
    <Layout
      language={language}
      seo={{
        ...seo,
        ogDescription: t('proProfile.ogDescription'),
      }}
      title={pageTitle}
    >
      {questionAnswers && questionAnswers.length > 0 && (
        <Head>
          <script
            type="application/ld+json"
            id="faq-structured-data"
            dangerouslySetInnerHTML={{
              __html: getFAQStructuredData(questionAnswers),
            }}
          />
        </Head>
      )}
      <div className="pro-profile-page-container">
        <ProfileClaim />
        <ProProfileContent
          featureFlags={featureFlags}
          internalBlogPosts={internalBlogPosts}
          jobTypeId={jobTypeId}
          slugFromQueryParam={slugFromQueryParam}
          setJobTypeTitle={setJobTypeTitle}
        />
      </div>
      <style jsx>
        {`
          .pro-profile-page-container {
            height: 100%;
          }

          @media screen and (min-width: 768px) {
            .pro-profile-page-container {
              background: #f5f5f5;
            }
          }
        `}
      </style>
    </Layout>
  );
};

export const getServerSideProps = wrapper.getServerSideProps(
  (store) =>
    async ({ req, defaultLocale, locale, query }) => {
      const { dispatch, getState, logicMiddleware } = store as Store<RootState> & {
        logicMiddleware: LogicMiddleware;
      };

      const cacheKeyProps = {
        locale,
        pageName: 'pro-profile',
        query,
        url: req.url ?? '',
      };
      const cachedProps = await getSSRCache(cacheKeyProps, 'props');
      const cachedStates = await getSSRCache(cacheKeyProps, 'states');

      if (cachedProps && cachedStates) {
        dispatch(setCachedStates(cachedStates as RootState));
        await logicMiddleware.whenComplete();
        return { props: cachedProps };
      }

      const { jobTypeId = null, userId } = query as { jobTypeId: string | null; userId: string };

      const { currentLocale, i18nData } = await setupPageAndLoadTranslations({
        defaultLocale,
        locale,
        namespaces: ['common', 'marketingPages', 'request', 'users', 'form'],
        // @ts-expect-error Extended store
        store,
      });

      dispatch(loadPro(userId));
      await logicMiddleware.whenComplete();
      const pro = proSelector(getState()) as
        | {
            converted?: boolean;
            deactivated?: boolean;
            enabledJobTypes: Array<EnabledJobTypesSelectorProps>;
            id: string;
            isDuplicated?: boolean;
            isItp?: boolean;
            jobProfiles?: Array<JobProfile>;
            location: { name: string };
            name: string;
            proId?: string;
            proProfile: JobProfile;
            profile: { avatar: string | undefined; languages: { en: boolean; ja: boolean } };
            reviews?: {
              counts: Array<number>;
              list: Array<ReviewContent>;
              total: number;
            };
            seoDescription?: string;
            slug?: string;
          }
        | undefined;

      if (!pro || pro.deactivated) {
        return { notFound: true };
      }

      // Will be fixed in next.js#27003
      // https://github.com/vercel/next.js/pull/27003
      const encodedSlug = encodeURIComponent(pro.slug ?? '');

      // if pro has slug and userId is pro's id then we redirect to page using slug
      if (encodedSlug && userId === pro.id) {
        return {
          redirect: {
            destination: `${currentLocale === 'en' ? '/en' : ''}/profile/${encodedSlug}/pro`,
            permanent: true,
          },
        };
      }
      if (pro.isItp && pro.converted && pro.proId) {
        return {
          redirect: {
            destination: `${currentLocale === 'en' ? '/en' : ''}/profile/${pro.proId}/pro`,
            permanent: true,
          },
        };
      }

      if (jobTypeId && jobTypeId !== '') {
        const generalizeResult = await jobTypeAliasesService.getAlias(jobTypeId);
        if (generalizeResult) {
          const generalizedUrl = generalizeResult.alias;
          return {
            redirect: {
              destination: `${
                currentLocale === 'en' ? '/en' : ''
              }/profile/${encodedSlug}/pro/${generalizedUrl}`,
              permanent: false,
            },
          };
        }
      }

      if (
        encodedSlug &&
        userId === pro.id &&
        jobTypeId &&
        pro.enabledJobTypes.find((jobTypeItem) => jobTypeItem.id === jobTypeId)
      ) {
        return {
          redirect: {
            destination: `${
              currentLocale === 'en' ? '/en' : ''
            }/profile/${encodedSlug}/pro/${jobTypeId}`,
            permanent: true,
          },
        };
      }

      const seo: SeoObject = {};

      const serviceProfile = pro.jobProfiles?.find((profile) => profile.jobTypeId === jobTypeId);

      const isReviewsQAEnabled = await featureFlagsService.isReviewsQAEnabled();

      seo.noIndex = shouldAddNoindex(
        pro.name,
        pro.isDuplicated,
        pro.proProfile.description,
        serviceProfile?.description,
        pro.jobProfiles,
        pro.proProfile,
        jobTypeId,
        pro.reviews?.list
      );
      if (!seo.noIndex) {
        seo.description = pro.seoDescription;
      }

      if (!seo.noIndex) {
        seo.canonicalUrl = createCanonicalUrl(
          pro.proProfile.description,
          serviceProfile?.description,
          currentLocale,
          encodedSlug,
          jobTypeId,
          pro.jobProfiles,
          pro.proProfile,
          pro.reviews?.list
        );
      }

      const questionAnswers: Array<QuestionAnswer> =
        pro.jobProfiles
          ?.find(
            (jobProfile: { jobTypeId: string | undefined }) =>
              jobProfile.jobTypeId === query.jobTypeId
          )
          ?.questionAnswers?.filter((questionAnswer) => questionAnswer.answer) ?? [];

      const featureFlags = { isReviewsQAEnabled };

      const title = pro.name ? `${pro.name} | ${pro.location.name}` : '';
      let jobTypeTitle = '';

      if (jobTypeId && pro.jobProfiles) {
        jobTypeTitle = getJobTypeTitle(jobTypeId, pro.jobProfiles);
      }

      let internalBlogPostItems: Array<InternalBlogPostItem> = [];
      if (jobTypeId) {
        internalBlogPostItems = await getInternalBlogPostsByJobTypeId({
          jobTypeId,
        });
      }

      const serverSideProps = {
        featureFlags,
        internalBlogPosts: internalBlogPostItems,
        ...i18nData,
        jobProfiles: pro.jobProfiles ?? [],
        jobTypeId,
        jobTypeTitle,
        questionAnswers,
        seo,
        slugFromQueryParam: userId,
        title,
      };

      setSSRCache(cacheKeyProps, serverSideProps, 'props');
      setSSRCache(cacheKeyProps, getState(), 'states');

      return { props: serverSideProps };
    }
);

export default ProProfilePage;
